import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { Menu, X } from "lucide-react";

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const nav = useNavigate();
  const publicLinks = [
    { to: "/come-funziona", label: "Come funziona" },
    { to: "/servizi", label: "Servizi" },
    { to: "/settori", label: "Settori" },
    { to: "/pricing", label: "Prezzi" },
  ];
  const appLinks = [
    { to: "/dashboard", label: "Dashboard", id: "dashboard" },
    { to: "/azienda", label: "Azienda", id: "azienda" },
    { to: "/business-check", label: "Business Check", id: "business-check" },
    { to: "/report", label: "Report", id: "report" },
    { to: "/cfo-simulator", label: "CFO Simulator", id: "cfo" },
    { to: "/report#opportunita", label: "Opportunity", id: "opportunity" },
    { to: "/report#obiettivi", label: "Goals", id: "goals" },
    { to: "/impostazioni", label: "Impostazioni", id: "settings" },
  ];
  const links = user ? appLinks : publicLinks;
  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#E5E4EB]" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO_URL} alt="ImpresaChiara" className="h-8 md:h-11 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map(l => (
            <NavLink key={l.to} to={l.to}
              data-testid={`nav-${l.id || l.to.slice(1)}`}
              className={({isActive}) => `px-3 py-2 rounded-full text-sm font-semibold transition ${isActive ? 'text-[#5424D5] bg-[#EDE8FC]' : 'text-[#171522] hover:bg-[#F4F4F7]'}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <Link to="/dashboard" className="ic-btn-ghost text-sm" data-testid="nav-dashboard">Dashboard</Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="ic-btn-primary text-sm" data-testid="nav-logout">Esci</button>
            </>
          ) : (
            <>
              <Link to="/login" className="ic-btn-ghost text-sm" data-testid="nav-login">Accedi</Link>
              <Link to="/business-check" className="ic-btn-primary text-sm" data-testid="nav-cta">Analizza gratis</Link>
            </>
          )}
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#E5E4EB] bg-white px-4 py-3 flex flex-col gap-2" data-testid="mobile-menu">
          {links.map(l => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 font-semibold">{l.label}</Link>
          ))}
          {user ? (
            <button onClick={async () => { await logout(); nav("/"); setOpen(false); }} className="ic-btn-ghost text-center" data-testid="mobile-logout">Esci</button>
          ) : (
            <Link to="/business-check" onClick={() => setOpen(false)} className="ic-btn-primary text-center">Analizza gratis</Link>
          )}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#171522] text-white pt-16 pb-10 px-4 sm:px-8 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-8">
        <div>
          <img src={LOGO_URL} alt="ImpresaChiara" className="h-12 bg-white rounded-xl p-2 mb-4" />
          <p className="text-sm text-white/70 max-w-xs">Conosci la tua impresa. Migliorala. Numeri, insight e decisioni per micro e piccole imprese italiane.</p>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF6A24] mb-3">Piattaforma</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/come-funziona">Come funziona</Link></li>
            <li><Link to="/servizi">Servizi</Link></li>
            <li><Link to="/settori">Settori</Link></li>
            <li><Link to="/pricing">Prezzi</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF6A24] mb-3">Strumenti</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/business-check">Business Check</Link></li>
            <li><Link to="/benchmark">Benchmark</Link></li>
            <li><Link to="/simulatore">Simulatore</Link></li>
            <li><Link to="/business-plan">Business Plan</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF6A24] mb-3">Legale</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/termini">Termini di Servizio</Link></li>
            <li><Link to="/cookie">Cookie Policy</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#FF6A24] mb-3">Contatti</p>
          <p className="text-sm text-white/80">hello@impresachiara.it</p>
          <p className="text-xs text-white/50 mt-6">© {new Date().getFullYear()} ImpresaChiara · Tutti i diritti riservati.</p>
        </div>
      </div>
    </footer>
  );
}

export function StatusBadge({ status }) {
  const map = {
    ACTIVE: { cls: "status-active", label: "Attivo", dot: "#10B981" },
    LOCKED: { cls: "status-locked", label: "Piano superiore", dot: "#6E6B7B" },
    COMING_SOON: { cls: "status-coming", label: "In arrivo", dot: "#FF6A24" },
  };
  const s = map[status] || map.ACTIVE;
  return (
    <span className={`ic-badge ${s.cls}`} data-testid={`status-${status.toLowerCase()}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{background: s.dot}} />{s.label}
    </span>
  );
}
